import CryptoJS from 'crypto-js';
import { useMemo, useState } from 'react';
import { getAttrFromProduct, ATTR_NAMES } from '../../../../types/attrNames';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { generateEmailHash } from '../../../../utils/emailHash';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import {
  matchApplyV3ModalIntent,
  useApplyV3Modal,
  useCtaUrl,
} from '../../../ApplyCta';
import Button from '../../../Button';
import { Checkbox } from '../../Inputs/Checkbox';
import { EXCLUSIVE_REWARDS_TRANSLATIONS } from '../i18n';
import { FooterProps } from '../types';
import { EmailInput } from './Input';

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const removeSubDomain = (hostname: string): string => {
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return parts.slice(-2).join('.');
  }
  return hostname;
};

const Footer: React.FC<FooterProps> = (props) => {
  const {
    product,
    onClose: handleBack,
    onCreateLead: createLead,
    pushDataLayer,
  } = props;

  const context = useCmsClientContext();
  const staticText = getClientTranslations(
    EXCLUSIVE_REWARDS_TRANSLATIONS,
    context,
  );

  const rewardProductIdAttr = getAttrFromProduct(
    ATTR_NAMES.REWARD_PRODUCT_ID,
    product ?? undefined,
  );
  const rewardProductId = rewardProductIdAttr?.value;

  const { open: openApplyV3Modal } = useApplyV3Modal();
  const ctaHref = useCtaUrl({ product: product ?? undefined });
  const [email, setEmail] = useState('');
  const [isEmailError, setIsEmailError] = useState<boolean>();
  const [subscribeNewsletter, setSubscribeNewsletter] =
    useState<boolean>(false);

  const [isLeadFetching, setIsLeadFetching] = useState(false);

  const providerUrl = useMemo(() => {
    if (ctaHref && ctaHref.length > 0 && ctaHref.indexOf('deeplink') >= 0) {
      const deepLinkURL = new URL(ctaHref);
      const deepLinkParams = deepLinkURL.searchParams;

      const destinationUrl = deepLinkParams.get('destination') ?? '';
      if (deepLinkParams.get('destination')) {
        const destUrl = new URL(destinationUrl);
        return removeSubDomain(destUrl.hostname) ?? '';
      }
    } else if (ctaHref && ctaHref.length > 0) {
      const destUrl = new URL(ctaHref);
      return removeSubDomain(destUrl.hostname) ?? '';
    }
    return '';
  }, [ctaHref]);

  const onSubmit = async () => {
    if (!email) {
      setIsEmailError(true);
      return;
    }

    if (!product || !rewardProductId || isEmailError) {
      return;
    }

    if (ctaHref) {
      // No `<ApplyCtaLink>` here (`window.open` in a submit handler), so the
      // modal opens imperatively. CI products normally go to external
      // providers; this guards a future apply-v3 CI URL.
      const modalIntent = matchApplyV3ModalIntent(ctaHref);
      if (modalIntent) {
        openApplyV3Modal(modalIntent);
      } else {
        window.open(ctaHref, '_blank', 'noopener,noreferrer');
      }
    }

    setIsLeadFetching(true);
    try {
      const response = await createLead({
        email,
        productId: Number(rewardProductId),
        consent: subscribeNewsletter,
        source: 'extra1',
        searchParams: location.search,
      });

      const emailInLowerCase = email.toLowerCase();
      const emailMd5 = email?.length ? generateEmailHash(email) : '';
      const emailSha256 = email?.length
        ? CryptoJS.SHA256(emailInLowerCase).toString()
        : '';

      const userData = {
        email: emailInLowerCase || '',
        email_hash_md5: emailMd5,
        email_hash_sha256: emailSha256,
      };

      const trackingData = {
        event: 'reward_gateway',
        location: 'rewards page',
        step_number: '1',
        step_name: 'user-auth',
        intent: 'Lead Submitted',
        marketing_consent: subscribeNewsletter ? 'yes' : 'no',
        lead_id: String(response?.leadId || ''),
        reward_campaign_name: '',
        reward_campaign_id: rewardProductId,
        reward_selected: '',
        user_data: userData,
        userIdentities: structuredClone(userData),
        items: [
          {
            item_id: product.uid ?? '',
            item_name: product.product_name ?? '',
            item_brand: product.provider?.provider_name ?? '',
          },
        ],
      };
      pushDataLayer(trackingData);

      handleBack();
      if (response?.redemptionUrl) {
        window.open(response.redemptionUrl, '_self');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLeadFetching(false);
    }
  };

  return (
    <div className="flex flex-row sticky bottom-0 py-8 lg:py-6 justify-evenly bg-gray-50 md:bg-white z-20 border-t border-gray-300">
      <div className="w-full flex flex-col lg:w-fit lg:px-0 md:px-6 sm:px-4">
        <div className="flex flex-col md:flex-row gap-2 md:gap-4">
          <div className="flex grow items-start justify-between flex-col gap-2 md:w-115 lg:w-max lg:min-w-max">
            <p className="alpha-overline text-gray-900">
              {staticText.shareEmail}
            </p>
            <EmailInput
              placeholder={
                isEmailError ? staticText.emailError : 'your@email.com'
              }
              type="email"
              value={email}
              onChange={(value) => setEmail(value)}
              // height={isMobile ? 36 : isTablet ? 40 : 44}
              onInput={() => {
                if (isEmailError) setIsEmailError(undefined);
              }}
              error={isEmailError}
              validateInput={() => {
                email.trim().length > 0 &&
                  setIsEmailError(!validateEmail(email));
              }}
            />
          </div>
          <div className="flex flex-col items-start justify-between gap-2 md:w-61 lg:w-fit lg:min-w-fit">
            {!!providerUrl && (
              <p className="alpha-caption text-gray-700 md:block hidden">
                {staticText.redirect} {providerUrl}
              </p>
            )}
            <Button
              styleType="cta"
              onClick={onSubmit}
              disabled={!!isEmailError || isLeadFetching}
              className="h-9 md:h-10 lg:h-11"
            >
              {isLeadFetching ? (
                <span className="flex items-center gap-2">
                  <span className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                </span>
              ) : (
                staticText.buy
              )}
            </Button>
          </div>
        </div>
        <div className="md:mt-4 sm:mt-2 flex flex-col gap-2 lg:w-page-max lg:self-center">
          <Checkbox
            onClick={() => setSubscribeNewsletter(!subscribeNewsletter)}
            containerClass="!size-5"
            defaultValue={subscribeNewsletter}
            label={
              <span className="text-text/primary alpha-body-2">
                {staticText.subscribe}{' '}
                <a
                  href="https://www.singsaver.com.sg/page/singsaver-privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-800 underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {staticText.privacyPolicy}
                </a>
              </span>
            }
          />
          <div className="w-full border-b border-b-gray-300 hidden md:block" />
          <span className="self-center alpha-caption text-gray-700 md:block sm:hidden">
            {staticText.termsBy}{' '}
            <a
              href="https://www.singsaver.com.sg/page/singsaver-terms"
              target="_blank"
              rel="noreferrer"
              className="underline text-primary-800"
            >
              {staticText.termsAndConditions}
            </a>{' '}
            {staticText.and}{' '}
            <a
              href="https://www.singsaver.com.sg/page/singsaver-privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="underline text-primary-800"
            >
              {staticText.privacyPolicyFooter}
            </a>
          </span>
          {!!providerUrl && (
            <p className="alpha-caption text-gray-700 sm:block md:hidden">
              {staticText.redirect} {providerUrl}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Footer;
